/**
 * LaunchPortalContractSection — Phase 1 initial-contract signing.
 *
 * Thin adapter: fetches the current contract from the token-gated
 * public endpoints and dispatches acceptance through the shared
 * ``<ContractReviewAndSign>`` component (extracted in [22]) so the
 * signing UX matches the Hub-authenticated flow byte-for-byte.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";

import ContractReviewAndSign from "@/components/contracts/ContractReviewAndSign";

const API = process.env.REACT_APP_BACKEND_URL;

// Empty file-headers object stabilised at module scope so pdfjs'
// ``file`` useMemo (deps ``[fileUrl, fileHeaders]``) NEVER
// recomputes across re-renders. An inline ``{}`` would tear down
// and reload the Document every time the signing panel state
// changes — which is what caused the [21f] scroll flicker.
const EMPTY_HEADERS = Object.freeze({});

async function fetchJson(url, opts = {}) {
  const res = await fetch(url, { credentials: "include", ...opts });
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const j = await res.json();
      detail = j.detail || detail;
    } catch {
      /* noop */
    }
    const err = new Error(typeof detail === "string" ? detail : JSON.stringify(detail));
    err.status = res.status;
    throw err;
  }
  return res.json();
}

export default function LaunchPortalContractSection({ slug, onSigned }) {
  const [loading, setLoading] = useState(true);
  const [contract, setContract] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loadErr, setLoadErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setLoadErr("");
    setNotFound(false);
    try {
      const data = await fetchJson(`${API}/api/public/launch-portal/${slug}/contract`);
      setContract(data);
    } catch (e) {
      if (e.status === 404) {
        setNotFound(true);
        setContract(null);
      } else {
        setLoadErr(e.message);
      }
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  // Stable ``onAccept`` — passed into <ContractReviewAndSign>. Kept
  // above the early returns so React's rules-of-hooks are satisfied
  // regardless of the load state. Not strictly required (CRAS
  // doesn't put it in a useEffect dep), but keeping the ref stable
  // insulates against future changes there.
  const handleAccept = useCallback(async (payload) => {
    await fetchJson(`${API}/api/public/launch-portal/${slug}/contract/accept`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    await load();
    onSigned && onSigned();
  }, [slug, load, onSigned]);

  if (loading) {
    return (
      <div
        className="flex items-center gap-2 text-sm text-stone-500 py-8 justify-center"
        data-testid="launch-contract-loading"
      >
        <Loader2 className="w-4 h-4 animate-spin" /> Loading your contract…
      </div>
    );
  }

  if (notFound) {
    return (
      <div
        className="rounded-lg border border-stone-200 bg-stone-50 p-4 text-sm text-stone-700"
        data-testid="launch-contract-not-issued"
      >
        Your contract will appear here once it has been issued by Head Office.
      </div>
    );
  }

  if (loadErr) {
    return (
      <div
        className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 flex items-center gap-2"
        data-testid="launch-contract-load-error"
      >
        <AlertTriangle className="w-4 h-4" />
        {loadErr}
      </div>
    );
  }

  if (!contract) return null;

  const variant = contract.status === "signed" ? "signed" : "personalised";
  const pdfUrl = `${API}/api/public/launch-portal/${slug}/contract/pdf?variant=${variant}`;

  return (
    <div className="space-y-4" data-testid="launch-contract-panel">
      {/* Signing explanation — sits directly below the "Contract"
          section heading rendered by <LaunchPortalOverlay>. Styling
          matches other Launch Portal intro paragraphs
          (text-stone-700 leading-relaxed). */}
      <p
        className="text-stone-700 leading-relaxed"
        data-testid="launch-contract-signing-explainer"
      >
        You can read the contract on screen using the page controls, or
        download a copy. Once you are happy, review and accept your
        proposed territory, tick the box to confirm that you agree to
        the contract, draw your signature in the box provided, then
        click &lsquo;Accept and sign contract&rsquo;.
      </p>

      <div className="flex items-center gap-2 flex-wrap text-xs text-stone-600">
        <span
          data-testid={`launch-contract-status-${contract.status}`}
          className={
            "inline-flex items-center px-2 py-0.5 rounded-full border font-medium " +
            (contract.status === "signed"
              ? "bg-sky-50 text-sky-800 border-sky-300"
              : "bg-emerald-50 text-emerald-800 border-emerald-300")
          }
        >
          {contract.status === "signed" ? "Signed" : "Awaiting your acceptance"}
        </span>
        {contract.contract_reference && (
          <span className="text-xs text-stone-500">Ref: {contract.contract_reference}</span>
        )}
        {contract.issued_at && (
          <span className="text-xs text-stone-500">
            Issued {new Date(contract.issued_at).toLocaleString("en-GB")}
          </span>
        )}
      </div>

      <ContractReviewAndSign
        contract={contract}
        pdfUrl={pdfUrl}
        // Module-scope frozen empty object — stable reference across
        // every render so pdfjs' ``file`` useMemo doesn't recompute.
        pdfFileHeaders={EMPTY_HEADERS}
        downloadUrl={pdfUrl}
        testidPrefix="launch-contract-review"
        viewerMinHeight={520}
        // Launch Portal v2 — one-page-at-a-time viewer with Prev/Next
        // + genuine browser fullscreen. HUB /portal/contracts stays
        // on the legacy scroll viewer.
        viewerPaginated
        onAccept={handleAccept}
      />
    </div>
  );
}
